import { normalize } from "common/lib/normalize";
import { isPaymentActive } from "common/lib/paymentOptions";
import { getPayment } from "common/services/payments.service";
import * as moment from "moment";
import PaymentHeader from "pages/Payments/ViewPayment/PaymentHeader";
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";

interface PaymentDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  paymentId: string | null;
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
  isOpen,
  toggle,
  paymentId,
}) => {
  const [payment, setPayment] = React.useState<any>({});
  const [, setLoading] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPayment(paymentId, {});
      setPayment(normalize(response.data));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const status = useMemo(() => {
    return isPaymentActive(payment?.startDate, payment?.endDate);
  }, [payment.startDate, payment.endDate]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader toggle={toggle} className="bg-light p-3">
        View Payment Details
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col lg={12}>
            <PaymentHeader payment={payment} fetchData={fetchData} />
          </Col>
          <Col lg={12}>
            <CardBody className="p-4">
              <Row className="g-3">
                <Col lg={3} xs={6}>
                  <p className="text-muted mb-2 text-uppercase fw-semibold">
                    Position Type
                  </p>
                  <h5 className="fs-14 mb-0">
                    <span id="invoice-no">{payment?.positionType?.name}</span>
                  </h5>
                </Col>
                <Col lg={3} xs={6}>
                  <p className="text-muted mb-2 text-uppercase fw-semibold">
                    Payment / Contract Period
                  </p>
                  <h5 className="fs-14 mb-0">
                    <span id="invoice-date">
                      {moment(new Date(payment?.startDate)).format("DD MMM Y")}
                    </span>{" "}
                    -{" "}
                    <span id="invoice-date">
                      {moment(new Date(payment?.endDate)).format("DD MMM Y")}
                    </span>
                  </h5>
                </Col>
                <Col lg={3} xs={6}>
                  <p className="text-muted mb-2 text-uppercase fw-semibold">
                    Status
                  </p>

                  <React.Fragment>
                    {status === null ? (
                      <span className="badge bg-warning-subtle text-default fs-11">
                        Not Provided
                      </span>
                    ) : status ? (
                      <span className="badge bg-success-subtle text-success fs-11">
                        Active
                      </span>
                    ) : status === false ? (
                      <span className="badge bg-danger-subtle text-danger fs-11">
                        Not Active
                      </span>
                    ) : null}
                  </React.Fragment>
                </Col>
                <Col lg={3} xs={6}>
                  <p className="text-muted mb-2 text-uppercase fw-semibold">
                    Reporting Agency
                  </p>
                  <Link to={`#`} className="">
                    <h5 className="fs-14 mb-0 ">{payment?.agency?.name}</h5>
                  </Link>
                </Col>
              </Row>
            </CardBody>
          </Col>
          <Col lg={12}>
            <CardBody className="p-4 border-top border-top-dashed">
              <Row className="g-3">
                <Col sm={6}>
                  <h6 className="text-muted text-uppercase fw-semibold mb-3">
                    Individual / Recipient Details
                  </h6>

                  <div
                    className="table-responsive border border-dashed rounded p-2  mb-3 h-100"
                    // style={{ minHeight: "200px" }}
                  >
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td colSpan={2}>
                            <p
                              className="fw-medium mb-0 fs-14"
                              id="billing-name"
                            >
                              <Link
                                to={`/individuals/${payment?.individual?.id}`}
                                className=""
                              >
                                <i className="ri-user-line align-bottom me-1"></i>{" "}
                                {payment?.individual?.fullName ||
                                  "Not Provided"}
                              </Link>
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td
                            className="fw-medium text-muted"
                            style={{ whiteSpace: "nowrap", width: "25%" }}
                          >
                            Email Address:
                          </td>
                          <td>
                            {payment?.individual?.emailAddress ||
                              "Not Provided"}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Phone Number:
                          </td>
                          <td>
                            {payment?.individual?.phoneNumber || "Not Provided"}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Passport or ID Number:
                          </td>
                          <td>
                            {payment?.individual?.passportOrIdNumber ||
                              "Not Provided"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
                <Col sm={6}>
                  <h6 className="text-muted text-uppercase fw-semibold mb-3">
                    Position Details
                  </h6>

                  <div className="table-responsive border border-dashed rounded p-2  mb-3 h-100">
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <td
                            className="fw-medium text-muted"
                            style={{ whiteSpace: "nowrap", width: "30%" }}
                          >
                            {" "}
                            Position Title:
                          </td>
                          <td>{payment?.positionTitle || "Not Provided"}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Position Type:
                          </td>
                          <td>
                            {payment?.positionType?.name || "Not Provided"}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Payment Category:
                          </td>
                          <td>
                            {payment?.paymentCategory?.name || "Not Provided"}{" "}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Project or Fund:
                          </td>
                          <td>{payment?.projectOrFund || "Not Provided"} </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">Staff ID:</td>
                          <td>{payment?.staffId || "Not Provided"} </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Thematic Area:
                          </td>
                          <td>
                            {payment?.thematicArea?.name || "Not Provided"}{" "}
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">Sector:</td>
                          <td>{payment?.sector?.name || "Not Provided"} </td>
                        </tr>
                        <tr>
                          <td className="fw-medium text-muted">
                            Is Salary Aligned with Harmonized Scale:
                          </td>
                          <td>
                            {typeof payment?.isSalaryAligned === "boolean"
                              ? payment.isSalaryAligned
                                ? "Yes"
                                : "No"
                              : "Not Provided"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Col>
          <Col lg={12}>
            <CardBody className="p-4">
              <div className="table-responsive"></div>
              <div className="border-top border-top-dashed mt-2">
                <Table
                  className="table table-borderless table-nowrap align-middle mb-0 ms-auto"
                  style={{ width: "250px" }}
                >
                  <tbody>
                    <tr className="border-top border-top-dashed fs-15">
                      <th scope="row">Amount per month</th>
                      <th className="text-end">
                        ${" "}
                        {(payment?.amountPerMonth &&
                          payment?.amountPerMonth?.toLocaleString()) ||
                          "Not Provided"}
                      </th>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="mt-4">
                <div className="alert alert-info">
                  <p className="mb-0">
                    <span className="fw-semibold">NOTE:</span>
                    <span id="note">
                      {" "}
                      The data provided herein is shared by various agencies and
                      is intended solely for use within the United Nations
                      system. Unauthorized dissemination or distribution of this
                      data outside of the UN system is strictly prohibited.
                    </span>
                  </p>
                </div>
              </div>
              {/* <div className="hstack gap-2 justify-content-end d-print-none mt-4">
                <Link
                  to="#"
                  onClick={printInvoice}
                  className="btn btn-success"
                >
                  <i className="ri-printer-line align-bottom me-1"></i>{" "}
                  Print
                </Link>
                <Link to="#" className="btn btn-primary">
                  <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                  Download
                </Link>
              </div> */}
            </CardBody>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PaymentDetailsModal;
